import { FC } from 'react';
import { Box, InputBase, Link, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { useTrans } from '@libs/i18n';
import { LinkSimpleIcon } from '@phosphor-icons/react';

import type { ProfileReferenceValue } from '../../../api/types';
import { editBoxSx } from '../../utils/tokens';

import type { FieldInputProps } from './fieldInput.types';

/**
 * Shared styling for the three editable inputs. The typed value reads as dark
 * text; only the placeholder keeps the muted tone, so a filled input is
 * obvious against an empty one.
 */
const inputSx = {
  typography: 'textSM_regular',
  color: 'text.primary',
  p: 0,
  '& input::placeholder, & textarea::placeholder': {
    color: 'text.tertiary',
    opacity: 1,
  },
} as const;

/** Coerce an unknown form value into the composite shape (or an empty draft). */
function asReference(value: unknown): { text: string; url: string; note: string } {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const v = value as Partial<ProfileReferenceValue>;
    return {
      text: typeof v.text === 'string' ? v.text : '',
      url: typeof v.source?.url === 'string' ? v.source.url : '',
      note: typeof v.source?.note === 'string' ? v.source.note : '',
    };
  }
  return { text: '', url: '', note: '' };
}

/**
 * Renderer for a `reference`-type field: a composite value of an AI-extracted /
 * user-editable `text` plus an optional source pointer (`url` and/or free-text
 * `note`). Value shape is `ProfileReferenceValue` (`{ text, source }`).
 *
 * Emits `null` when the whole field is empty (no text, no url, no note) so an
 * untouched reference reads as unset and isn't counted as filled — matching the
 * backend `hasValue`. `source` is `null` unless a url or note is present.
 */
export const ReferenceField: FC<FieldInputProps> = observer(
  ({ field, value, mode, dirty, disabled, onChange }) => {
    const { t } = useTrans();
    const { text, url, note } = asReference(value);
    const sourceRequired = field.validation?.sourceRequired === true;
    const isEmpty = text.trim() === '' && url === '' && note === '';

    const emit = (nextText: string, nextUrl: string, nextNote: string) => {
      const src: { url?: string; note?: string } = {};
      if (nextUrl !== '') src.url = nextUrl;
      if (nextNote !== '') src.note = nextNote;
      const source = Object.keys(src).length > 0 ? src : null;
      if (nextText.trim() === '' && source === null) {
        onChange?.(null);
        return;
      }
      onChange?.({ text: nextText, source });
    };

    // ── Read mode ──────────────────────────────────────────────────────────
    if (mode === 'read') {
      if (isEmpty) {
        return (
          <Typography
            variant="textSM_medium"
            color="text.disabled"
            sx={{ fontStyle: 'italic' }}
          >
            {t('app.orgProfile.field.noReference')}
          </Typography>
        );
      }
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography
            variant="textSM_medium"
            color="text.primary"
            sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
          >
            {text}
          </Typography>
          {url !== '' && (
            <Link
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                typography: 'textXS_regular',
                wordBreak: 'break-all',
              }}
            >
              <LinkSimpleIcon size={12} />
              {url}
            </Link>
          )}
          {note !== '' && (
            <Typography variant="textXS_regular" color="text.tertiary">
              {note}
            </Typography>
          )}
        </Box>
      );
    }

    // ── Edit mode ────────────────────────────────────────────────────────────
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
        <Box
          sx={{
            ...editBoxSx(dirty),
            px: 1.5,
            py: 1.1,
            display: 'flex',
            alignItems: 'flex-start',
          }}
        >
          <InputBase
            fullWidth
            multiline
            minRows={2}
            maxRows={6}
            value={text}
            disabled={disabled}
            placeholder={t('app.orgProfile.field.referenceText')}
            onChange={(e) => emit(e.target.value, url, note)}
            sx={inputSx}
          />
        </Box>

        <Typography variant="textXS_regular" color="text.tertiary">
          {sourceRequired
            ? t('app.orgProfile.field.sourceRequiredLabel')
            : t('app.orgProfile.field.sourceOptional')}
        </Typography>

        <Box sx={{ ...editBoxSx(dirty), px: 1.5, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <LinkSimpleIcon size={14} />
          <InputBase
            fullWidth
            type="url"
            value={url}
            disabled={disabled}
            placeholder={t('app.orgProfile.field.sourceUrl')}
            onChange={(e) => emit(text, e.target.value, note)}
            sx={inputSx}
          />
        </Box>

        <Box sx={{ ...editBoxSx(dirty), px: 1.5, py: 1 }}>
          <InputBase
            fullWidth
            value={note}
            disabled={disabled}
            placeholder={t('app.orgProfile.field.sourceNote')}
            onChange={(e) => emit(text, url, e.target.value)}
            sx={inputSx}
          />
        </Box>
      </Box>
    );
  },
);
